import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, User, Lock, UserCog, Briefcase } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulation d'une connexion
    setTimeout(() => {
      // En fonction de l'email, on redirige vers le bon profil
      if (formData.email.toLowerCase().includes('admin')) {
        navigate('/admin');
      } else if (formData.email.toLowerCase().includes('enqueteur')) {
        navigate('/enqueteur');
      } else {
        // Par défaut, rediriger vers admin
        navigate('/admin');
      }
      setIsLoading(false);
    }, 800);
  };

  const handleQuickAccess = (userType) => {
    setIsLoading(true);
    setTimeout(() => {
      navigate(`/${userType}`);
      setIsLoading(false);
    }, 400);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-atl-blue-50 via-white to-port-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo et titre */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-atl-blue-600 rounded-xl mb-4 shadow-lg">
            <Briefcase className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-port-gray-900 mb-2">
            Provelliance
          </h1>
          <p className="text-port-gray-600">
            Plateforme de gestion des enquêtes
          </p>
        </div>

        {/* Formulaire de connexion */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <h2 className="text-xl font-semibold text-port-gray-900 mb-6">
            Connexion
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-port-gray-700 mb-2">
                Adresse e-mail
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-port-gray-400" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-11 pr-4 py-3 border border-port-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atl-blue-500 focus:border-atl-blue-500 transition-colors"
                  placeholder="votre.email@exemple.fr"
                  required
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-port-gray-700 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-port-gray-400" />
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full pl-11 pr-4 py-3 border border-port-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-atl-blue-500 focus:border-atl-blue-500 transition-colors"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {/* Mot de passe oublié */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-atl-blue-600 focus:ring-atl-blue-500 border-port-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-port-gray-600">
                  Se souvenir de moi
                </span>
              </label>
              <button
                type="button"
                className="text-sm text-atl-blue-600 hover:text-atl-blue-700 font-medium"
              >
                Mot de passe oublié ?
              </button>
            </div>

            {/* Bouton de connexion */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-atl-blue-600 text-white py-3 rounded-lg font-medium hover:bg-atl-blue-700 focus:outline-none focus:ring-2 focus:ring-atl-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Connexion en cours...
                </span>
              ) : (
                <span className="flex items-center">
                  <LogIn className="h-5 w-5 mr-2" />
                  Se connecter
                </span>
              )}
            </button>
          </form>
        </div>

        {/* Accès rapide aux profils */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="text-center mb-4">
            <p className="text-sm font-medium text-port-gray-700">
              Accès rapide (démonstration)
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Bouton Admin */}
            <button
              onClick={() => handleQuickAccess('admin')}
              disabled={isLoading}
              className="group relative overflow-hidden bg-gradient-to-br from-atl-blue-500 to-atl-blue-600 text-white p-4 rounded-xl hover:from-atl-blue-600 hover:to-atl-blue-700 transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex flex-col items-center space-y-2">
                <div className="p-2 bg-white/20 rounded-lg group-hover:bg-white/30 transition-colors">
                  <UserCog className="h-6 w-6" />
                </div>
                <span className="text-sm font-semibold">
                  Administrateur
                </span>
              </div>
              <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-300"></div>
            </button>

            {/* Bouton Enquêteur */}
            <button
              onClick={() => handleQuickAccess('enqueteur')}
              disabled={isLoading}
              className="group relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-4 rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex flex-col items-center space-y-2">
                <div className="p-2 bg-white/20 rounded-lg group-hover:bg-white/30 transition-colors">
                  <Briefcase className="h-6 w-6" />
                </div>
                <span className="text-sm font-semibold">
                  Enquêteur
                </span>
              </div>
              <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-300"></div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-port-gray-500">
            © 2024 Provelliance. Tous droits réservés.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;

